/**
 * Hook that owns the cleanup-in-progress state so it survives unmount when
 * the user closes the dialog mid-run. Returns an API the dialog binds to.
 */
import { useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const pickField = (p) => p?.image_front_url || p?.image_url || p?.image_small_url || "";
const scoreProduct = (p, seedTokens) => {
  if (!pickField(p)) return -1;
  let score = 0;
  const tags = String(p?.countries_tags || "").toLowerCase();
  if (tags.includes("norway") || tags.includes("en:norway") || tags.includes("nb:norge")) score += 50;
  if ((p?.product_name_no || "").trim()) score += 10;
  if (p?.image_front_url) score += 15;
  if ((p?.brands || "").trim()) score += 5;
  const compl = parseFloat(p?.completeness || 0);
  if (!Number.isNaN(compl)) score += compl * 20;
  const name = `${p?.product_name_no || ""} ${p?.product_name || ""} ${p?.generic_name_no || ""}`.toLowerCase();
  let overlap = 0;
  for (const t of seedTokens) if (name.includes(t)) overlap++;
  if (seedTokens.length > 0) score += (overlap / seedTokens.length) * 40;
  if ((p?.quantity || "").trim()) score += 5;
  return score;
};

export function usePantryCleanup({ onChanged } = {}) {
  const [phase, setPhase] = useState("idle"); // idle | names | images | duplicates | review | done
  const [names, setNames] = useState({ count: 0, merged: 0 });
  const [duplicates, setDuplicates] = useState({ groups: 0 });
  const [imageSuggestions, setImageSuggestions] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [savingPicks, setSavingPicks] = useState(false);
  const cancelRef = useRef(false);

  const reset = useCallback(() => {
    setPhase("idle");
    setNames({ count: 0, merged: 0 });
    setDuplicates({ groups: 0 });
    setImageSuggestions([]);
    setProgress({ done: 0, total: 0 });
    cancelRef.current = false;
  }, []);

  const standardiseNames = useCallback(async () => {
    setPhase("names");
    try {
      const res = await api.standardizePantryNames(false);
      const applied = res.data?.applied || 0;
      const merged = res.data?.merged_groups || 0;
      setNames({ count: applied, merged });
      return { applied, merged };
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke standardisere navn");
      return { applied: 0, merged: 0 };
    }
  }, []);

  const fetchImageSuggestions = useCallback(async (includeWithImage) => {
    setPhase("images");
    try {
      const res = await api.getPantry();
      const allItems = res.data || [];
      const itemsTarget = includeWithImage
        ? allItems.filter((it) => it.name)
        : allItems.filter((it) => !it.image_url && it.name);
      setProgress({ done: 0, total: itemsTarget.length });
      const found = [];
      for (const it of itemsTarget) {
        if (cancelRef.current) break;
        const seed = it.name.trim();
        const seedTokens = seed.toLowerCase().split(/[\s,.\-/]+/).filter((t) => t.length >= 3);
        const products = [];
        const seenCodes = new Set();
        for (const u of [
          `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(seed)}&search_simple=1&action=process&json=1&page_size=8&sort_by=popularity_key&countries_tags_en=norway`,
          `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(seed)}&search_simple=1&action=process&json=1&page_size=8&sort_by=popularity_key`,
        ]) {
          try {
            const r = await fetch(u);
            if (!r.ok) continue;
            const j = await r.json();
            for (const p of j?.products || []) {
              const code = p?.code || p?._id;
              if (code && seenCodes.has(code)) continue;
              if (code) seenCodes.add(code);
              products.push(p);
            }
          } catch { /* skip */ }
        }
        const ranked = products
          .map((p) => ({ p, s: scoreProduct(p, seedTokens) }))
          .filter((x) => x.s >= 0)
          .sort((a, b) => b.s - a.s);
        const top = ranked[0]?.p;
        if (top) {
          const image = pickField(top);
          const suggestedName = (top.product_name_no || top.product_name || "").trim();
          found.push({
            id: it.id,
            name: it.name,
            suggestedName: suggestedName && suggestedName.toLowerCase() !== it.name.toLowerCase() ? suggestedName : "",
            image,
            picked: ranked[0].s >= 40,
            quality: Math.round(ranked[0].s),
          });
        }
        setProgress((p) => ({ ...p, done: p.done + 1 }));
        await new Promise((res2) => setTimeout(res2, 80));
      }
      setImageSuggestions(found);
      return found;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke hente bilete");
      return [];
    }
  }, []);

  const findAIDuplicates = useCallback(async () => {
    setPhase("duplicates");
    try {
      const res = await api.aiFindPantryDuplicates();
      const groups = (res.data?.groups || []).length;
      setDuplicates({ groups });
      return { groups };
    } catch {
      return { groups: 0 };
    }
  }, []);

  const runAll = useCallback(async ({ mode = "full", includeWithImage = false } = {}) => {
    if (phase !== "idle" && phase !== "done") return;
    cancelRef.current = false;
    if (mode !== "images") {
      await standardiseNames();
    }
    const suggestions = await fetchImageSuggestions(includeWithImage);
    if (mode !== "images") {
      await findAIDuplicates();
    }
    if (suggestions.length > 0) {
      setPhase("review");
      // Surface a toast in case the dialog is closed in the background
      toast.success(`Opprydding ferdig – ${suggestions.length} bilete-forslag klare for gjennomgang`);
    } else {
      setPhase("done");
      onChanged?.();
      toast.success("Opprydding ferdig");
    }
  }, [phase, standardiseNames, fetchImageSuggestions, findAIDuplicates, onChanged]);

  const togglePick = useCallback((id) => {
    setImageSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, picked: !s.picked } : s));
  }, []);

  const setUseSuggestedName = useCallback((id, useIt) => {
    setImageSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, useName: useIt } : s));
  }, []);

  const saveReviewedPicks = useCallback(async () => {
    const picks = imageSuggestions.filter((s) => s.picked);
    if (picks.length === 0) {
      setPhase("done");
      onChanged?.();
      return;
    }
    setSavingPicks(true);
    let ok = 0;
    let failed = 0;
    for (const s of picks) {
      try {
        const update = { image_url: s.image };
        if (s.useName && s.suggestedName) update.name = s.suggestedName;
        await api.updatePantryItem(s.id, update);
        ok++;
      } catch { failed++; }
    }
    setSavingPicks(false);
    if (ok > 0) toast.success(`${ok} varer oppdatert`);
    if (failed > 0) toast.error(`${failed} feila`);
    setPhase("done");
    onChanged?.();
  }, [imageSuggestions, onChanged]);

  const skipImages = useCallback(() => {
    setPhase("done");
    onChanged?.();
  }, [onChanged]);

  return {
    state: { phase, names, duplicates, imageSuggestions, progress, savingPicks },
    actions: { runAll, reset, togglePick, setUseSuggestedName, saveReviewedPicks, skipImages },
  };
}
